import React, {useEffect, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useUpdatedRef} from 'helpers/hooks/utils';
import StyledDatabaseEntitiesQueryBarWrapper
  from 'components/templates/Wrappers/Database/DatabaseEntitiesQueryBarWrapper/DatabaseEntitiesQueryBarWrapper.styles';
import DatabaseEntitiesQueryBar from 'components/organisms/Bars/DatabaseEntitiesQueryBar/DatabaseEntitiesQueryBar';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import utils from 'helpers/utils';
import {useLocation} from 'react-router-dom';
import dom from 'helpers/dom';

const DatabaseEntitiesQueryBarWrapper = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps({
    ToolbarComponent: DatabaseEntitiesQueryBar,
    ...props
  }, 'DatabaseEntitiesQueryBarWrapper', {
    static: ['closed']
  });

  const innerRef = useRef(null);
  const initialised = useRef(false);
  const tableProvider = useTable();

  const view = tableProvider?.view;
  const hasView = (view && (view?.id !== 'default'));
  const isEntityView = Boolean(tableProvider?.view?.activeViews?.find((v) => v.name === 'entity'));

  const closedRef = useUpdatedRef(!tableProvider.state?.showQuery || isEntityView);
  const [closed, setClosed] = useState(!tableProvider.state?.showQuery || isEntityView);

  useImperativeHandle(ref, () => innerRef.current);

  const openEvent = useEffectEvent(tableProvider.openQuery);
  const closeEvent = useEffectEvent(tableProvider.closeQuery);

  useEffect(() => {
    return utils.observeScroll(window, ({y}) => {
      const bBox = dom.getBbox(innerRef.current?.refs?.ref?.current?.querySelector?.(`.DatabaseEntitiesQueryBar`));
      if (tableProvider.state?.showQuery) {
        if (Math.round(bBox.height) > 0 && Math.round(y) >= Math.round(bBox.height)) {
          closeEvent?.();
        }
      } else {
        if (Math.round(bBox.height) > 0) {
          if (Math.round(y) < Math.round(bBox.height)) {
            openEvent?.();
          }
        }
      }
    });
  }, [openEvent, closeEvent, tableProvider.state?.showQuery, isEntityView]);

  const location = useLocation();
  useLayoutEffect(() => {
    const close = closedRef.current;
    setClosed((current) => {
      initialised.current = initialised.current || current !== close;
      return close;
    });
  }, [location, closedRef]);

  useLayoutEffect(() => {
    if (hasView) {
      initialised.current = true;
      setClosed(true);
      closeEvent?.();
    }
  }, [hasView, closeEvent]);

  useLayoutEffect(() => {
    const close = () => {
      const bBox = dom.getBbox(innerRef.current?.refs?.ref?.current?.querySelector?.('.DatabaseEntitiesQueryBar'));
      if (utils.isDefined(bBox?.height)) {
        const scroll = () => {
          if (window.scrollY < bBox.height) {
            window.scrollTo({top: bBox.height});
          }
        };

        return utils.observeRetry(scroll, 5);
      }
    }

    if (!isEntityView) {
      if (initialised.current) {
        if (closed) {
          setClosed(false);
        }
        return close();
      }
    }
  }, [closed, isEntityView]);

  innerProps.className = utils.flattenClassName(innerProps.className, {
    closed: closed
  });

  return <StyledDatabaseEntitiesQueryBarWrapper ref={innerRef} {...innerProps}
                                                variant="inset">
    {innerProps.children}
  </StyledDatabaseEntitiesQueryBarWrapper>
});

DatabaseEntitiesQueryBarWrapper.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default DatabaseEntitiesQueryBarWrapper;
